import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { copyText } from './browser';

/** Re-renders every `intervalMs` and returns the current (optionally offset) time. */
export function useNow(intervalMs = 250, offsetMs = 0): number {
  const [now, setNow] = useState(() => Date.now() + offsetMs);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now() + offsetMs), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, offsetMs]);
  return now;
}

function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

export const useIsMobile = () => useMediaQuery('(max-width: 767px)');

/** Phones held sideways (matches the `short:` CSS variant). */
export const useIsShort = () => useMediaQuery('(max-height: 500px)');

/** Calls `handler` on key presses, ignoring the ones typed into form fields. */
export function useHotkey(keys: string, handler: (event: KeyboardEvent) => void, enabled = true): void {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });
  useEffect(() => {
    if (!enabled) return;
    const accepted = keys.split(',');
    const listener = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (!accepted.includes(event.key) || event.repeat) return;
      event.preventDefault();
      handlerRef.current(event);
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [keys, enabled]);
}

/**
 * Wraps an async action so it can't run twice at once (a ref, not state: a key press and a click
 * can land in the same tick) and exposes its pending state and last error message.
 */
export function useGuardedAction(action: () => Promise<void>, fallbackError = 'Something went wrong.') {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const running = useRef(false);
  const latest = useRef(action);
  useEffect(() => {
    latest.current = action;
  });

  const run = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError(null);
    try {
      await latest.current();
    } catch (err) {
      setError(err instanceof Error ? err.message : fallbackError);
    } finally {
      running.current = false;
      setBusy(false);
    }
  }, [fallbackError]);

  return { run, busy, error };
}

/** Copies text to the clipboard and remembers which button did it, for a "✓ Copied" moment. */
export function useCopy<Key extends string>(): [copied: Key | null, copy: (text: string, key: Key) => void] {
  const [copied, setCopied] = useState<Key | null>(null);
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = useCallback((text: string, key: Key) => {
    void copyText(text).then((ok) => {
      if (!ok) return;
      setCopied(key);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(null), 1500);
    });
  }, []);
  return [copied, copy];
}
